import { useMemo, useCallback, useRef, memo } from "react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { FileText, Building2, BarChart3, PieChart, TrendingUp, Activity, DollarSign, Users, Target } from "lucide-react";
import { useIsMobile } from "@/hooks/useMediaQuery";

// Helper functions for icons and colors
const getGroupIcon = (groupName: string) => {
  const name = groupName?.toLowerCase() || '';
  
  // Weekly reports
  if (name.includes('tuần') || name.includes('weekly')) return TrendingUp;
  // Monthly reports  
  if (name.includes('tháng') || name.includes('month')) return BarChart3;
  // Quarterly reports
  if (name.includes('quý') || name.includes('quarter')) return PieChart;
  // Yearly reports
  if (name.includes('năm') || name.includes('year') || name.includes('annual')) return Activity;
  // Financial reports
  if (name.includes('tài chính') || name.includes('finance') || name.includes('financial')) return DollarSign;
  // HR reports
  if (name.includes('nhân sự') || name.includes('hr') || name.includes('human')) return Users;
  // Employee evaluation reports
  if (name.includes('đánh giá') || name.includes('dgnv') || name.includes('evaluation')) return Target;
  // Planning reports
  if (name.includes('kế hoạch') || name.includes('plan') || name.includes('target')) return Target;
  // General reports
  if (name.includes('báo cáo') || name.includes('report')) return FileText;
  
  // Default icon
  return Building2;
};

const getGroupColor = (groupName: string) => {
  const name = groupName?.toLowerCase() || '';
  if (name.includes('tuần') || name.includes('weekly')) return { icon: 'text-green-600', badge: 'bg-green-100 text-green-800' };
  if (name.includes('tháng') || name.includes('month')) return { icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-800' };
  if (name.includes('quý') || name.includes('quarter')) return { icon: 'text-orange-600', badge: 'bg-orange-100 text-orange-800' };
  if (name.includes('năm') || name.includes('year')) return { icon: 'text-red-600', badge: 'bg-red-100 text-red-800' };
  if (name.includes('tài chính') || name.includes('finance')) return { icon: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-800' };
  if (name.includes('nhân sự') || name.includes('hr')) return { icon: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-800' };
  if (name.includes('đánh giá') || name.includes('dgnv') || name.includes('evaluation')) return { icon: 'text-pink-600', badge: 'bg-pink-100 text-pink-800' };
  return { icon: 'text-gray-600', badge: 'bg-gray-100 text-gray-800' };
};

interface ReportGroupsSidebarProps {
  reportGroups: any[];
  activeTab: string;
  currentPageReportCounts: { total: number; byGroup: { [key: string]: number } };
  isDrawerOpen?: boolean;
  onTabChange: (tab: string) => void;
  onDrawerClose?: () => void;
}

interface GroupItemProps {
  group: any;
  isActive: boolean;
  reportCount: number;
  onSelect: (groupId: string) => void;
}

const GroupItem = ({ group, isActive, reportCount, onSelect }: GroupItemProps) => {
  const handleClick = () => {
    const groupId = group.ID || group.GroupID || group.id || 'all';
    onSelect(groupId.toString());
  };

  // Special case for "All Reports"
  if (group.ID === 'all') {
    return (
      <div 
        onClick={handleClick}
        className={`flex items-center gap-2 rounded-md cursor-pointer transition-colors duration-150 mb-1 border px-2 py-2 ${
          isActive 
            ? 'bg-blue-50 border-blue-200 shadow-sm' 
            : 'hover:bg-gray-50 border-transparent'
        }`}
      >
        <div className="shrink-0">
          <FileText className="h-4 w-4 text-blue-600" aria-hidden />
        </div>
        <p
          className={`min-w-0 flex-1 truncate text-xs font-medium leading-none ${isActive ? 'text-blue-900' : 'text-gray-900'}`}
          title={`${group.GroupName} — ${group.Descrt}`}
        >
          {group.GroupName}
        </p>
        <Badge variant="secondary" className="shrink-0 bg-blue-100 px-1.5 py-0 text-[0.625rem] text-blue-800 tabular-nums">
          {reportCount}
        </Badge>
      </div>
    );
  }

  // Regular group item
  const IconComponent = getGroupIcon(group.GroupName || group.GroupNameShort || '');
  const colors = getGroupColor(group.GroupName || group.GroupNameShort || '');
  
  const title = group.GroupName || group.GroupNameShort || 'Nhóm không tên';
  const tooltip = [title, group.Descrt?.trim(), `${reportCount} báo cáo`]
    .filter(Boolean)
    .join(' — ');

  return (
    <div 
      onClick={handleClick}
      className={`flex items-center gap-2 rounded-md cursor-pointer transition-colors duration-150 mb-1 border px-2 py-2 ${
        isActive 
          ? 'bg-gray-50 border-gray-200 shadow-sm' 
          : 'hover:bg-gray-50 border-transparent'
      }`}
    >
      <div className="shrink-0">
        <IconComponent className={`h-4 w-4 ${colors.icon}`} aria-hidden />
      </div>
      <p
        className={`min-w-0 flex-1 truncate text-xs font-medium leading-none ${isActive ? 'text-gray-900' : 'text-gray-700'}`}
        title={tooltip}
      >
        {title}
      </p>
      <Badge variant="secondary" className={`shrink-0 px-1.5 py-0 text-[0.625rem] tabular-nums ${colors.badge}`}>
        {reportCount}
      </Badge>
    </div>
  );
};

const ReportGroupsSidebar = memo(({
  reportGroups,
  activeTab,
  currentPageReportCounts,
  isDrawerOpen = false,
  onTabChange,
  onDrawerClose
}: ReportGroupsSidebarProps) => {
  // 🎯 Enhanced render tracking for debugging
  const renderCount = useRef(0);
  const lastRenderTime = useRef(Date.now());
  const renderStartTime = Date.now();
  renderCount.current++;
  
  console.log(`🔄 ReportGroupsSidebar render #${renderCount.current}`, {
    activeTab,
    reportGroupsCount: reportGroups?.length || 0,
    timeSinceLastRender: renderStartTime - lastRenderTime.current,
    timestamp: new Date().toISOString().slice(11, 23)
  });
  
  lastRenderTime.current = renderStartTime;

  const isMobile = useIsMobile();

  const sortedReportGroups = useMemo(() => {
    return [...reportGroups].sort((a: any, b: any) => (a.STT || 0) - (b.STT || 0));
  }, [reportGroups]);

  const handleGroupSelect = useCallback((groupId: string) => {
    onTabChange(groupId);
    if (isMobile && onDrawerClose) onDrawerClose();
  }, [onTabChange, isMobile, onDrawerClose]);

  // 🎯 MEMOIZED: Stable sidebar content
  const SidebarContent = useCallback(() => (
    <div className="h-full flex flex-col">
      <div className="flex-1 space-y-1 overflow-y-auto pr-1">
        {/* All Reports Tab */}
        <GroupItem
          group={{
            ID: 'all',
            GroupID: 'all',
            GroupName: 'Tất cả báo cáo',
            Descrt: `${currentPageReportCounts?.total || 0} báo cáo`
          }}
          isActive={activeTab === 'all'}
          reportCount={currentPageReportCounts?.total || 0}
          onSelect={handleGroupSelect}
        />

        {/* Report Groups */}
        {sortedReportGroups.map((group) => {
          const groupId = (group.ID || group.GroupID || group.id || '').toString();
          const reportCount = currentPageReportCounts?.byGroup?.[groupId] || 0;
          
          return (
            <GroupItem
              key={groupId}
              group={group}
              isActive={activeTab === groupId}
              reportCount={reportCount}
              onSelect={handleGroupSelect}
            />
          );
        })}
      </div>
    </div>
  ), [isMobile, onDrawerClose, activeTab, currentPageReportCounts, sortedReportGroups, handleGroupSelect]);

  // Mobile: Sheet
  if (isMobile) {
    return (
      <Sheet open={isDrawerOpen} onOpenChange={(open) => !open && onDrawerClose?.()}>
        <SheetContent side="left" className="w-80 p-4">
          {SidebarContent()}
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Fixed Sidebar with smooth width transition
  return (
    <div
      className="bg-white border-r flex-shrink-0 overflow-hidden transition-[width] duration-200 ease-in-out"
      style={{ width: isDrawerOpen ? '20rem' : '0' }}
    >
      <div className="h-full w-80 p-4">
        {SidebarContent()}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // 🎯 OPTIMIZED: Only re-render when necessary props change
  const currentPageReportCountsEqual =
    JSON.stringify(prevProps.currentPageReportCounts) === JSON.stringify(nextProps.currentPageReportCounts);

  const propsEqual = {
    activeTab: prevProps.activeTab === nextProps.activeTab,
    isDrawerOpen: prevProps.isDrawerOpen === nextProps.isDrawerOpen,
    reportGroups: prevProps.reportGroups === nextProps.reportGroups,
    onTabChange: prevProps.onTabChange === nextProps.onTabChange,
    onDrawerClose: prevProps.onDrawerClose === nextProps.onDrawerClose,
    currentPageReportCounts: currentPageReportCountsEqual,
  };

  const shouldSkipRender = Object.values(propsEqual).every(Boolean);

  if (!shouldSkipRender) {
    const changedProps = Object.entries(propsEqual)
      .filter(([_, isEqual]) => !isEqual)
      .map(([propName]) => propName);
    
    console.log(`🔄 ReportGroupsSidebar memo: WILL RE-RENDER due to:`, changedProps);
    
    // 🔍 DEEP ANALYSIS: Log actual values for debugging
    changedProps.forEach(propName => {
      if (propName === 'currentPageReportCounts') {
        console.log(`📊 currentPageReportCounts CHANGED:`, {
          prev: prevProps.currentPageReportCounts,
          next: nextProps.currentPageReportCounts
        });
      } else if (propName === 'reportGroups') {
        console.log(`📋 reportGroups CHANGED:`, {
          prevLength: prevProps.reportGroups?.length,
          nextLength: nextProps.reportGroups?.length,
          same: prevProps.reportGroups === nextProps.reportGroups
        });
      } else if (['onTabChange', 'onDrawerClose'].includes(propName)) {
        console.log(`🎯 FUNCTION ${propName} CHANGED - function references different`);
      } else {
        console.log(`🔧 ${propName} CHANGED:`, {
          prev: prevProps[propName],
          next: nextProps[propName]
        });
      }
    });
  } else {
    console.log(`✅ ReportGroupsSidebar memo: SKIP RE-RENDER - all props equal`);
  }
  
  return shouldSkipRender;
});

export default ReportGroupsSidebar;